import { HeaderContext } from '@tanstack/react-table';
import { capitalize } from 'lodash';
import { IndexedTransaction, PolymorphicTransaction } from 'types/domain';

type TransactionTableKey =
    | 'contact_name'
    | 'external_id'
    | 'external_label'
    | 'address_address_1'
    | 'address_address_2'
    | 'address_city'
    | 'address_state'
    | 'address_zipcode'
    | 'address_country'
    | 'public_message'
    | keyof IndexedTransaction;

function isIndexedTransaction(transaction: any): transaction is IndexedTransaction {
    return !!transaction.payment_method;
}

const getPaymentMethodFromPolymorphicTransaction = (transaction: PolymorphicTransaction) => {
    if (isIndexedTransaction(transaction)) {
        const paymentMethod = transaction.payment_method;
        return {
            method: paymentMethod?.method,
            method_subtype: paymentMethod?.method_subtype,
            cc_last_four: paymentMethod?.cc_last_four,
            cc_expire: paymentMethod?.cc_expire,
        };
    }

    return {
        method: transaction?.method,
        method_subtype: transaction?.method_subtype,
        cc_last_four: transaction?.cc_last_four,
        cc_expire: transaction?.cc_expire,
    };
};

const curriedTransformTableKeyToReadable = (header: HeaderContext<IndexedTransaction, unknown>) =>
    transformTableKeyToReadable(header.column.id as TransactionTableKey);

const transformTableKeyToReadable = (key: TransactionTableKey) => {
    switch (key) {
        case 'address_address_1':
            return 'Street 1';
        case 'address_address_2':
            return 'Street 2';
        case 'address_city':
            return 'City';
        case 'address_state':
            return 'State';
        case 'address_zipcode':
            return 'Zip';
        case 'address_country':
            return 'Country';
        case 'contact_name':
            return 'Contact';
        case 'external_id':
            return 'External ID';
        case 'external_label':
            return 'External label';
        case 'soft_credits':
            return 'Soft credits';
        case 'public_message':
            return 'Public message';
        default:
            return transactionKeyToReadable(key);
    }
};

const transactionKeyToReadable = (key: keyof IndexedTransaction) => {
    switch (key) {
        case 'status_friendly':
            return 'Status';
        case 'check_deposited_at':
            return 'Check deposit date';
        case 'transacted_at':
            return 'Date';
        case 'discount':
            return 'Promo code';
        case 'address':
            return 'Billing address';
        case 'first_name':
        case 'last_name':
        case 'created_at':
        case 'payment_method':
        case 'check_number':
        case 'team_member':
        case 'internal_note':
            return capitalize(key.split('_').join(' '));
        case 'campaign':
        case 'phone':
        case 'email':
        case 'amount':
        case 'dedication':
        case 'company':
        case 'number':
        case 'contact':
        case 'status':
        case 'fund':
        case 'team':
            return capitalize(key);
        case 'id':
        case 'plan_id':
        case 'contact_id':
        case 'external_ids':
        case 'acknowledged_at':
        case 'integration_status':
        default:
            return key;
    }
};

export {
    isIndexedTransaction,
    getPaymentMethodFromPolymorphicTransaction,
    transactionKeyToReadable,
    transformTableKeyToReadable,
    curriedTransformTableKeyToReadable,
};
export type { TransactionTableKey };
